import React, { useState, useRef, useEffect } from "react";
import IconButton from "@mui/material/IconButton";
import CircularProgress from "@mui/material/CircularProgress";
import Tooltip from "@mui/material/Tooltip";

/**
 * An icon button that shows its own spinner while its work is running.
 *
 * Every row action on the Online Policy table calls the server before anything
 * visible happens — retry re-reads the policy, edit re-checks it, view fetches
 * it. On a slow connection those take a second or two with no sign that the
 * click registered, so people click again; a second retry is a second job.
 *
 * The spinner replaces the icon rather than sitting next to it, so the row does
 * not change width and the table stops jumping while a request is in flight.
 * The button is disabled for the same period, which is what actually prevents
 * the double submit — the spinner only explains why.
 *
 * Usage is the same as an IconButton; onClick may be async and is awaited.
 *
 *   <AsyncIconButton title="Retry Policy" color="#2e7d32" hoverBg="#edf7ed"
 *                    onClick={async () => { await retry(row); }}>
 *     <ReplayIcon fontSize="small" />
 *   </AsyncIconButton>
 */
const AsyncIconButton = ({
  title,
  onClick,
  children,
  color,
  hoverBg,
  size = "small",
  spinnerSize = 18,
  disabled = false,
  sx = {},
  ...rest
}) => {
  const [busy, setBusy] = useState(false);
  // A row can be re-rendered out of existence by the very refresh its action
  // triggered. Setting state after that logs a React warning and, worse, hides
  // a real error behind it — so the unmount is tracked.
  //
  // The flag MUST be re-armed on mount, not just cleared on unmount. Under
  // React 18 StrictMode every component is mounted, unmounted and mounted
  // again in development: that first simulated unmount ran the cleanup, and
  // with nothing setting it back the ref stayed false for the component's whole
  // life. `setBusy(false)` was then skipped forever, so the button kept its
  // spinner and stayed disabled after the first click. It only looked fine
  // before because every action ended in a table reload that replaced the row
  // with a freshly-mounted one; an action that returns without reloading — such
  // as opening a confirmation dialog the user then cancels — left the spinner
  // stuck on screen.
  const alive = useRef(true);
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);

  const handleClick = async (event) => {
    if (busy || disabled) return;
    setBusy(true);
    try {
      await onClick?.(event);
    } finally {
      if (alive.current) setBusy(false);
    }
  };

  const button = (
    <span>
      {/* A disabled MUI button fires no pointer events, so the Tooltip needs a
          plain wrapper to listen on — without the span the tooltip disappears
          exactly while the spinner is showing and the user most wants it. */}
      <IconButton
        size={size}
        onClick={handleClick}
        disabled={busy || disabled}
        sx={{
          color,
          "&:hover": hoverBg ? { backgroundColor: hoverBg } : undefined,
          // Keep the footprint identical between the icon and the spinner so
          // the action column does not reflow mid-click.
          "&.Mui-disabled": { color },
          ...sx,
        }}
        {...rest}
      >
        {busy ? (
          <CircularProgress size={spinnerSize} thickness={5} sx={{ color }} />
        ) : (
          children
        )}
      </IconButton>
    </span>
  );

  return title ? (
    // disableInteractive: the hint closes as soon as the pointer leaves the icon (it does not stay up over the next one)
    <Tooltip title={busy ? "Working…" : title} disableInteractive>
      {button}
    </Tooltip>
  ) : (
    button
  );
};

export default AsyncIconButton;
